/**
 * Contracts for module "mcp". Owner: partners.
 * Scoped, expiring agency API keys for MCP clients and the MCP tool catalogue with required scopes.
 * Import as `@qm/contracts/mcp`.
 */
import { z } from 'zod';

export const MCP_SCOPES = ['read:clients', 'read:reports', 'write:drafts'] as const;
export const McpScopeSchema = z.enum(MCP_SCOPES);
export type McpScope = z.infer<typeof McpScopeSchema>;

export const KeyCreateSchema = z.object({
  name: z.string().trim().min(2).max(80),
  scopes: z.array(McpScopeSchema).min(1).max(MCP_SCOPES.length),
  expiresInDays: z.number().int().min(1).max(365).default(90),
});

export const KEY_PREFIX = 'qmk_';
/** Revocation is read from D1 on every call (no cache), so it applies to the next call: well inside 60 s. */
export const KEY_REVOCATION_TARGET_MS = 60_000;

export interface McpToolDef { name: string; scope: McpScope; description: string; inputSchema: Record<string, unknown> }
export const MCP_TOOLS: readonly McpToolDef[] = [
  { name: 'list_clients', scope: 'read:clients', description: 'List client workspaces the key owner manages.', inputSchema: { type: 'object', properties: {} } },
  { name: 'get_client', scope: 'read:clients', description: 'Get one managed client workspace.', inputSchema: { type: 'object', properties: { clientId: { type: 'string' } }, required: ['clientId'] } },
  {
    name: 'create_draft_post', scope: 'write:drafts', description: 'Create a draft post for a managed client. It enters the approval queue; it is never published directly.',
    inputSchema: { type: 'object', properties: { clientId: { type: 'string' }, channel: { type: 'string' }, title: { type: 'string' }, body: { type: 'string' } }, required: ['clientId', 'channel', 'title', 'body'] },
  },
];

export const DraftInputSchema = z.object({
  clientId: z.string().min(1).max(64),
  channel: z.enum(['facebook', 'instagram', 'linkedin', 'tiktok', 'x', 'whatsapp', 'email', 'blog']),
  title: z.string().trim().min(1).max(200),
  body: z.string().trim().min(1).max(5000),
});

export const JsonRpcRequestSchema = z.object({
  jsonrpc: z.literal('2.0'),
  id: z.union([z.string(), z.number()]).nullable().optional(),
  method: z.string().min(1).max(100),
  params: z.record(z.string(), z.unknown()).optional(),
});

export const MCP_EVENTS = { draftSubmitted: 'mcp.draft.submitted' } as const;
